/**
 * CR-6435: the view bar under the filter footer on the customer Calendar —
 * Add event and the Period dropdown on the left, Day / Week / Month and the
 * Calendar / List toggle on the right, per the design. The Period dropdown is
 * not a design field but is existing behaviour (default Upcoming) and lives
 * here so the filter sections match the design exactly.
 */
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { CalendarIcon, List, Plus } from 'lucide-react';
import type { CalendarViewType, ViewMode, EventPeriod } from './types';

export interface CalendarViewBarProps {
  viewMode: ViewMode;
  calendarView: CalendarViewType;
  eventPeriod: EventPeriod;
  canAddEvent: boolean;
  onAddEvent: () => void;
  onViewModeChange: (mode: ViewMode) => void;
  onCalendarViewChange: (view: CalendarViewType) => void;
  onEventPeriodChange: (period: EventPeriod) => void;
}

export function CalendarViewBar({
  viewMode, calendarView, eventPeriod, canAddEvent, onAddEvent,
  onViewModeChange, onCalendarViewChange, onEventPeriodChange,
}: CalendarViewBarProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        {canAddEvent && (
          <Button size="sm" onClick={onAddEvent}>
            <Plus className="h-4 w-4 mr-2" />
            Add event
          </Button>
        )}
        <Select value={eventPeriod} onValueChange={(value) => onEventPeriodChange(value as EventPeriod)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Upcoming" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="upcoming">Upcoming</SelectItem>
            <SelectItem value="last_3_months">Last 3 Months</SelectItem>
            <SelectItem value="last_6_months">Last 6 Months</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-3">
        {viewMode === 'calendar' && (
          <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
            {(['day', 'week', 'month'] as const).map((view) => (
              <Button
                key={view}
                variant={calendarView === view ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onCalendarViewChange(view)}
              >
                {view.charAt(0).toUpperCase() + view.slice(1)}
              </Button>
            ))}
          </div>
        )}
        <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewModeChange('calendar')}
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            Calendar
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewModeChange('list')}
          >
            <List className="h-4 w-4 mr-2" />
            List
          </Button>
        </div>
      </div>
    </div>
  );
}
